"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { StartupSvg, InvestorSvg, AdvisorSvg } from "@/lib/svg";
import { useState } from "react";
import { IconCircleDashedCheck } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import useWaitList from "@/hooks/useWaitList";
import { User } from "@/types/waitlist/types";
import { useRouter } from "next/navigation";
const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
});

export default function UserForm({
  setNavigation,
  setUser,
}: {
  setNavigation: (value: string) => void;
  setUser: (value: User) => void;
}) {
  const router = useRouter();
  const { addToWaitlist, sendEmail } = useWaitList();
  const [role, setRole] = useState<string>("founder");
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  //   const router = useRouter();
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
    },
  });

  const { mutateAsync: addWaitlist, isPending } = useMutation({
    mutationFn: addToWaitlist,
    onSuccess: (response) => {
      if (response.message === "founder") {
        setError(true);
        setErrorMessage("Your founder form is already registered!");
      } else if (response.message === "investor") {
        setError(true);
        setErrorMessage("Your investor form is already registered!");
      } else if (response.message === "advisor error") {
        setError(true);
        setErrorMessage("You Adivisor profile is already on the Wait List!");
      } else {
        if (response.data.role === "advisor") {
          router.push("/success");
        } else {
          sendEmail({
            username: `${response.data.firstName} ${response.data.lastName}`,
            email: response.data.email,
          });
          setUser(response.data);
          setNavigation(role);
        }
      }
    },
    onError: () => {
      setError(true);
      setErrorMessage("Something went wrong! Please try again later!");
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    await addWaitlist({
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      role: role,
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted p-6">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-4 text-4xl font-bold  bg-clip-text text-transparent bg-gradient-to-b from-neutral-200 to-neutral-600 tracking-tight">
            Join the Waitlist
          </h1>
          <p className="text-lg text-muted-foreground">
            Get early access offers and discounts. Sign up now to secure your
            spot.
          </p>
        </div>

        <div className="rounded-xl border bg-card p-6 shadow-lg">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="text-sm font-medium mb-3">Select your Role</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div
                  className={`relative flex flex-col items-center gap-2 border rounded-lg py-4 px-2 cursor-pointer ${
                    role === "founder" ? "border-orange-500" : "border-grey-500"
                  }`}
                  onClick={() => setRole("founder")}
                >
                  {role === "founder" && (
                    <IconCircleDashedCheck className="absolute top-[-5px] right-[-5px] bg-background rounded-full text-orange-500 z-10" />
                  )}
                  <StartupSvg />
                  <p
                    className={`text-xs ${
                      role === "founder" ? "text-orange-500" : "text-grey-500"
                    }`}
                  >
                    I'M A FOUNDER
                  </p>
                </div>
                <div
                  className={`relative flex flex-col justify-center items-center gap-2 border rounded-lg py-4 px-2 cursor-pointer ${
                    role === "investor"
                      ? "border-orange-500"
                      : "border-grey-500"
                  }`}
                  onClick={() => setRole("investor")}
                >
                  {role === "investor" && (
                    <IconCircleDashedCheck className="absolute top-[-5px] right-[-5px] bg-background rounded-full text-orange-500 z-10" />
                  )}
                  <InvestorSvg />
                  <p
                    className={`text-xs ${
                      role === "investor" ? "text-orange-500" : "text-grey-500"
                    }`}
                  >
                    I'M AN INVESTOR
                  </p>
                </div>
                <div
                  className={`relative flex flex-col items-center gap-2 border rounded-lg py-4 px-2 cursor-pointer ${
                    role === "advisor" ? "border-orange-500" : "border-grey-500"
                  }`}
                  onClick={() => setRole("advisor")}
                >
                  {role === "advisor" && (
                    <IconCircleDashedCheck className="absolute top-[-5px] right-[-5px] bg-background rounded-full text-orange-500 z-10" />
                  )}
                  <AdvisorSvg />
                  <p
                    className={`text-xs ${
                      role === "advisor" ? "text-orange-500" : "text-grey-500"
                    }`}
                  >
                    I'M AN ADVISOR
                  </p>
                </div>
              </div>

              <Button
                disabled={isPending}
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-300 font-semibold cursor-pointer"
              >
                {isPending ? "Joining..." : "Join Waitlist"}
              </Button>
              {error && (
                <div className="w-full flex flex-col items-center">
                  <div className="text-red-500  font-semibold">
                    {errorMessage}
                  </div>
                  <div className="text-muted-foreground">
                    Our team will reach out to you via email soon.
                  </div>
                </div>
              )}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
